import { C_Camera } from "../../camera/domain/camera.model";
import { ChangesDetection } from "../../../../change-detection";
import { I_UnitResponse } from "../../../../unit/api/unit.response";
import { C_PatientLocation } from "../../patient/domain/patient-location.model";
import { I_AdmissionResponse } from "../../admission/api/admission.response";
import { I_DischargeResponse } from "../../discharge/api/discharge.response";
import {
  I_LightBeaconConfigResponse, I_PointOfCareResponse, I_PointOfCareConfigurationResponse,
  I_PocBlockConfigResponse
} from "../api/point-of-care-config.response";
import { asyncScheduler, distinctUntilChanged, map, Observable, of, shareReplay, startWith, switchMap, throttleTime } from "rxjs";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { E_PoCStatus } from "../../../../../enums/point-of-care-status.enum";
import { I_AlarmResponse } from "../../../../alarm/api/alarm-config.response";


export class C_PointOfCareList extends ChangesDetection<T_PointOfCareListChange> {

  private list: Map<string, C_PointOfCareInfo> = new Map()

  private pendingRequests: I_PoCPendingActions[] = [];
  // Sets para control de duplicados en pendientes
  private pendingAdmissionIds: Set<string> = new Set();
  private pendingDischargeIds: Set<string> = new Set();
  private cachedPoCSubs = new Map<string, Observable<C_PointOfCareInfo | undefined>>();

  constructor() {
    super();
  }

  clear() {
    this.list.clear();
    this.forceUpdateDetection();
  }

  size() {
    return this.list.size;
  }

  getValues(): C_PointOfCareInfo[] {
    return Array.from(this.list.values());
  }

  get(id?: string | null): C_PointOfCareInfo | undefined {
    if (!id) return;
    return this.list.get(id);
  }

  setPocFromResponse(info: I_PointOfCareResponse) {
    if (!this.list.has(info.id)) {
      this.list.set(info.id, new C_PointOfCareInfo(info));
      this.forceUpdateDetection({ action: E_ChangeAction.Add, id: info.id });

    } else if (info) {
      this.list.get(info.id)?.updatePocConfig(info)
      this.forceUpdateDetection({ action: E_ChangeAction.Update, id: info.id });
    };
    this.checkPendingRequests();
  }

  updatePoc(poc: I_PointOfCareResponse) {
    this.list.get(poc.id)?.updatePocConfig(poc);
    this.forceUpdateDetection({ action: E_ChangeAction.Update, id: poc.id });
  }

  setPocLayoutId(pocId: string, layoutId: string) {
    this.list.get(pocId)?.setLayoutDefinitionId(layoutId);
  }

  setPointOfCare(poc: C_PointOfCareInfo) {
    if (!poc?.pocConfig.id) return;

    const id = poc.pocConfig.id;
    if (this.list.has(id)) {
      this.list.get(id)?.updateFromPoc(poc);
      this.forceUpdateDetection({ action: E_ChangeAction.Update, id });
    } else {
      this.list.set(id, poc);
      this.forceUpdateDetection({ action: E_ChangeAction.Add, id });
    }
  }

  deletePointOfCare(id: string) {
    if (this.list.has(id)) {
      this.list.delete(id);
      this.forceUpdateDetection({ action: E_ChangeAction.Delete, id });
    }
  }

  addAdmission(info: I_AdmissionResponse) {
    if (!info.pointOfCareId) return;
    const poc = this.list.get(info.pointOfCareId);

    if (!poc) {
      if (!this.pendingAdmissionIds.has(info.id!)) {
        this.pendingAdmissionIds.add(info.id!);
        this.pendingRequests.push({ action: E_PendingPocAction.AddAdmission, data: info });
      }
      return;
    }
    this.pendingAdmissionIds.delete(info.id!);
    if (poc.pocConfig.admissionId == undefined)
      poc.pocConfig.admissionId = info.id;
    poc.pocConfig.forceUpdateDetection();
  }

  updateAdmission(info: I_AdmissionResponse) {
    this.addAdmission(info);
  }

  deleteAdmission(info: I_AdmissionResponse) {
    if (!info.pointOfCareId) return;
    const poc = this.list.get(info.pointOfCareId);

    if (!poc) {
      if (!this.pendingAdmissionIds.has(info.id!)) {
        this.pendingAdmissionIds.add(info.id!);
        this.pendingRequests.push({ action: E_PendingPocAction.DeleteAdmission, data: info });
      }
      return;
    }

    this.pendingAdmissionIds.delete(info.id!);
    if (poc.pocConfig.admissionId === info.id) {
      poc.pocConfig.admissionId = undefined;
      poc.pocConfig.forceUpdateDetection();
    }
  }
  addDischarge(info: I_DischargeResponse) {
    if (!info.pointOfCareId) return;
    const poc = this.list.get(info.pointOfCareId);

    if (!poc) {
      if (!this.pendingDischargeIds.has(info.id)) {
        this.pendingDischargeIds.add(info.id);
        this.pendingRequests.push({ action: E_PendingPocAction.AddDischarge, data: info });
      }
      return;
    };
    this.pendingDischargeIds.delete(info.id);
    poc.pocConfig.dischargeId = info.id;
    poc.pocConfig.forceUpdateDetection();
  }

  deleteDischarge(info: I_DischargeResponse) {
    if (!info.pointOfCareId) return;
    const poc = this.list.get(info.pointOfCareId);

    if (!poc) {
      if (!this.pendingDischargeIds.has(info.id)) {
        this.pendingDischargeIds.add(info.id);
        this.pendingRequests.push({ action: E_PendingPocAction.DeleteDischarge, data: info });
      }
      return;
    }

    this.pendingDischargeIds.delete(info.id);
    if (poc.pocConfig.dischargeId === info.id) {
      poc.pocConfig.dischargeId = undefined;
      poc.pocConfig.forceUpdateDetection();
    }
  }

  private checkPendingRequests() {
    if (this.pendingRequests.length === 0) return;

    // Copiamos y vaciamos para evitar ciclos infinitos durante la iteración
    const currentBatch = [...this.pendingRequests];
    this.pendingRequests = [];

    currentBatch.forEach(pr => {
      // Al re-procesar, eliminamos de los Sets de control para permitir que
      // si vuelve a fallar (porque el POC aún no existe) se vuelva a encolar una sola vez.
      if (pr.data.id) {
        this.pendingAdmissionIds.delete(pr.data.id);
        this.pendingDischargeIds.delete(pr.data.id);
      }

      switch (pr.action) {
        case E_PendingPocAction.AddAdmission:
          this.addAdmission(pr.data as I_AdmissionResponse);
          break;
        case E_PendingPocAction.AddDischarge:
          this.addDischarge(pr.data as I_DischargeResponse);
          break;
        case E_PendingPocAction.DeleteAdmission:
          this.deleteAdmission(pr.data as I_AdmissionResponse);
          break;
        case E_PendingPocAction.DeleteDischarge:
          this.deleteDischarge(pr.data as I_DischargeResponse);
          break;
      }
    });
  }

  setAlarm(obs: I_AlarmResponse) {
    Array.from(this.list.values()).forEach((v: C_PointOfCareInfo) => {
      if (v.pocConfig.patientId != obs.patientId) return;
      const id = `${obs.patientId}_${obs.type}_${obs.value}`;
      v.pocConfig.alarmIds.add(id);
      v.pocConfig.forceUpdateDetection();
      //     const currentObs: C_ObservationAlarmData | undefined = v.pocConfig.alarms.list[obs.value!];
      //     if (currentObs != undefined && currentObs!.time! < obs!.time!) {
      //       currentObs.updateValueFromClass(obs);
      //       v.pocConfig.alarms.forceUpdateDetection();
      //     } else {
      //       v.pocConfig.alarms.list![obs.value!] = obs
      //       v.pocConfig.alarms.forceUpdateDetection();
      //     }
      //     return;
      //   }
    });
  }

  getSensorPoC(sensorsPocName: string): C_PointOfCareInfo | undefined {
    let sensorPoc;
    Array.from(this.list.values()).forEach((v) => {
      if (v.pocConfig.bed == sensorsPocName) sensorPoc = v;
    })
    return sensorPoc;
  }


  /**
     * Obtiene el flujo reactivo optimizado para un POC específico buscando por su Cama/Sensor
     */
  getLivePoCByBed(bedName: string): Observable<C_PointOfCareInfo | undefined> {
    if (!bedName) return of(undefined);

    // Si ya existe el flujo en caché, lo reutilizamos
    if (this.cachedPoCSubs.has(bedName)) {
      return this.cachedPoCSubs.get(bedName)!;
    }

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      // Cada vez que la lista sufra una alteración estructural, recalculamos cuál es el POC asignado
      map(() => this.getSensorPoC(bedName)),
      distinctUntilChanged(), // Solo continúa si cambia la referencia de la instancia de C_PointOfCareInfo
      switchMap((poc) => {
        if (!poc) return of(undefined);

        // Saltamos al flujo reactivo interno e independiente que ya gestiona la propia clase C_PointOfCareInfo
        return poc.getLivePoc().pipe(
          startWith(poc)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedPoCSubs.set(bedName, stream$);
    return stream$;
  }

  // Opcional: Limpieza de subs si fuese necesario en resets masivos
  clearPoCSubs() {
    this.cachedPoCSubs.clear();
  }

  reset(pocs: C_PointOfCareInfo[]) {
    this.list.clear();
    pocs.forEach(p => this.list.set(p.pocConfig.id!, p));
    this.forceUpdateDetection({ action: E_ChangeAction.Setup });
  }

}


export type T_PointOfCareListChange = {
  action: E_ChangeAction;
  id?: string; // ID del POC afectado
};


interface I_PoCPendingActions {
  action: E_PendingPocAction;
  data: I_AdmissionResponse | I_DischargeResponse;
}
enum E_PendingPocAction {
  DeleteAdmission = 'deleteAdm',
  AddAdmission = 'addAdm',
  DeleteDischarge = 'deleteDis',
  AddDischarge = 'addDis',
}

export class C_PointOfCareInfo extends ChangesDetection<T_PointOfCareChange> {
  layoutDefinitionId?: string;
  pocConfig: C_PointOfCareConfig = new C_PointOfCareConfig();

  private cachedPocStream$?: Observable<C_PointOfCareInfo>;

  constructor(info?: I_PointOfCareResponse) {
    super();
    if (!info) return;
    this.updatePocConfig(info);
  }
  setLayoutDefinitionId(id: string) {
    if (this.layoutDefinitionId == id) return;
    this.layoutDefinitionId = id;
    this.forceUpdateDetection({ action: E_PointOfCareChangeAction.LayoutChange });
  }
  updatePocConfig(info: I_PointOfCareResponse) {
    this.pocConfig.initialize(info);
    this.forceUpdateDetection({ action: E_PointOfCareChangeAction.UpdateConfig });
  }

  updateFromPoc(poc: C_PointOfCareInfo) {
    this.pocConfig = poc.pocConfig
    if (poc.layoutDefinitionId) this.setLayoutDefinitionId(poc.layoutDefinitionId);
    this.forceUpdateDetection({ action: E_PointOfCareChangeAction.RecreatePoc });
  }

  getLivePoc(): Observable<C_PointOfCareInfo> {
    if (this.cachedPocStream$) return this.cachedPocStream$;

    this.cachedPocStream$ = this.pocConfig.getLiveConfig().pipe(
      switchMap(() => {
        return this.getForcedChanges().pipe(
          startWith(null),
          map(() => this)
        );
      }),
      throttleTime(10, asyncScheduler, { leading: false, trailing: true }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.cachedPocStream$;
  }

}

export type T_PointOfCareChange =
  | { action: E_PointOfCareChangeAction.LayoutChange }
  | { action: E_PointOfCareChangeAction.RecreatePoc }
  | { action: E_PointOfCareChangeAction.UpdateConfig }

export enum E_PointOfCareChangeAction {
  LayoutChange = 'layoutChange',
  RecreatePoc = 'recreatePoc',
  UpdateConfig = 'updateConfig',
}

export class C_PointOfCareConfig extends ChangesDetection {
  id?: string
  room?: string;
  bed?: string;
  name?: string;
  unitName?: string;
  unitId?: string;
  configuration?: C_PointOfCareConfiguration = new C_PointOfCareConfiguration();
  unit?: I_UnitResponse;
  location: C_PatientLocation = new C_PatientLocation();
  alarmIds: Set<string> = new Set<string>();
  hasPatient?: boolean;
  patientId?: string;
  status: E_PoCStatus = E_PoCStatus.AVAILABLE;
  admissionId?: string;
  dischargeId?: string;

  private cachedStream$?: Observable<C_PointOfCareConfig>;

  constructor(info?: I_PointOfCareResponse) {
    super();
    this.initialize(info);
  }

  initialize(info?: I_PointOfCareResponse) {
    if (!info) return;
    if (info.id) this.id = info.id;
    if (info.room) this.room = info.room;
    if (info.bed) this.bed = info.bed;
    if (info.hasOwnProperty('unitName')) this.unitName = info.unitName;
    if (info.hasOwnProperty('unitId')) this.unitId = info.unitId;
    if (info.configuration) {
      this.configuration ? this.configuration.initialize(info.configuration) :this.configuration = new C_PointOfCareConfiguration(info.configuration);
    }
    if (info.unit) this.unit = info.unit;
    if (info.location) {
      this.location.initialize(info.location);
      if (!this.location.unitName) this.location.unitName = this.unitName ?? this.unit?.Name
    }
    if (info.hasPatient !== null) this.hasPatient = info.hasPatient;
    if (info.hasOwnProperty('patientid')) this.patientId = info.patientid;
    if (info.status !== null) this.status = info.status;
    if (info.hasOwnProperty('admissionId')) this.admissionId = info.admissionId;
    this.forceUpdateDetection();
  }

  getLiveConfig(): Observable<C_PointOfCareConfig> {
    if (this.cachedStream$) return this.cachedStream$;
    this.cachedStream$ = this.getForcedChanges().pipe(
      startWith(this),
      map(() => this),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    return this.cachedStream$;
  }

}

export class C_PointOfCareConfiguration extends ChangesDetection {
  id?: number;
  beacons: C_LightBeaconConfig[] = [];
  cameras: C_Camera[] = [];
  pocBlockConfig: C_PocBlockConfig = new C_PocBlockConfig();
  type?: string;

  constructor(json?: I_PointOfCareConfigurationResponse) {
    super();
    this.initialize(json);
  }

  initialize(json?: I_PointOfCareConfigurationResponse) {
    this.pocBlockConfig ? this.pocBlockConfig.initialize(json?.pocBlockConfig) : this.pocBlockConfig = new C_PocBlockConfig(json?.pocBlockConfig);

    if (!json) return;
    if (json.id !== undefined) this.id = json.id;
    if (json.beaconList) {
      this.beacons = [];
      json.beaconList?.forEach(beacon => {
        this.beacons.push(new C_LightBeaconConfig(beacon));
      });
    }
    if (json.cameraList) {
      this.cameras = [];
      json.cameraList?.forEach(camera => {
        this.cameras.push(new C_Camera(camera));
      });
    }
    if (json.type !== undefined) {
      this.type = json.type;
    }
  }

}

export class C_LightBeaconConfig extends ChangesDetection {
  // TODO
  constructor(json: I_LightBeaconConfigResponse) {
    super();
    if (!json) return;
  }
}

export class C_PocBlockConfig extends ChangesDetection {
  isActive: boolean = false;
  blockDateUntil?: Date;
  constructor(json?: I_PocBlockConfigResponse) {
    super();
    this.initialize(json);
  }

  initialize(json?: I_PocBlockConfigResponse) {
    if (json?.isActive != null) this.isActive = json.isActive;
    if (json?.blockDateUntil != null) this.blockDateUntil = new Date(json.blockDateUntil);
  }
}
